import { emitTreeGovernorEvent } from '@/lib/graph/graphMutationAudit';

export type KinsCircleRelationshipFamily =
  | 'bloodline'
  | 'spouse-partner'
  | 'co-parent'
  | 'step-family'
  | 'in-law'
  | 'guardian'
  | 'adoptive'
  | 'chosen-family'
  | 'household'
  | 'custom';

export type KinsCircleConnectorKind =
  | 'none'
  | 'self'
  | 'child'
  | 'parent'
  | 'spouse-partner'
  | 'shared-child'
  | 'household'
  | 'custom';

export type TreeGovernorDecisionRecord = {
  ruleApplied: string;
  reason: string;
  confidence: number;
  runtimeEvidenceId: string;
  suggestedNextAction: string;
  autoRepairConsidered: boolean;
};

export type TreeGovernorEventPayload = {
  action: string;

  name?: string | null;
  displayName?: string | null;
  nickname?: string | null;

  relationship?: string | null;
  relationshipOverride?: string | null;
  relationshipFamily?: KinsCircleRelationshipFamily;

  connectorRequired?: boolean;
  connectorKind?: KinsCircleConnectorKind;
  connectorSelected?: string | null;

  childId?: string | null;
  parentId?: string | null;
  spouseOrPartnerId?: string | null;
  memberId?: string | null;

  affectsBloodline?: boolean;
  affectsHousehold?: boolean;
  affectsLegalFamily?: boolean;
  affectsChosenFamily?: boolean;

  source?: string;
  decision?: TreeGovernorDecisionRecord;
  extra?: Record<string, unknown>;
};

export function inferRelationshipFamily(relationship?: string | null): KinsCircleRelationshipFamily {
  const r = `${relationship ?? ''}`.toLowerCase();

  if (r.includes('co-parent') || r.includes('coparent')) return 'co-parent';
  if (r.includes('step')) return 'step-family';
  if (r.includes('in-law') || r.includes('law')) return 'in-law';
  if (r.includes('spouse') || r.includes('wife') || r.includes('husband') || r.includes('partner')) return 'spouse-partner';
  if (r.includes('guardian')) return 'guardian';
  if (r.includes('adopt')) return 'adoptive';
  if (r.includes('chosen') || r.includes('god') || r.includes('friend')) return 'chosen-family';
  if (r.includes('household')) return 'household';
  if (r.includes('parent') || r.includes('child') || r.includes('sibling') || r.includes('grand') || r.includes('cousin') || r.includes('aunt') || r.includes('uncle')) return 'bloodline';

  return 'custom';
}

export function inferConnectorKind(relationship?: string | null): KinsCircleConnectorKind {
  const r = `${relationship ?? ''}`.toLowerCase();

  if (r.includes('co-parent') || r.includes('coparent')) return 'shared-child';
  if (r.includes('step')) return 'parent';
  if (r.includes('in-law') || r.includes('law')) return 'spouse-partner';
  if (r.includes('spouse') || r.includes('wife') || r.includes('husband') || r.includes('partner')) return 'spouse-partner';
  if (r.includes('guardian')) return 'child';
  if (r.includes('child')) return 'parent';
  if (r.includes('parent')) return 'child';
  if (r.includes('household')) return 'household';

  return 'none';
}

export function relationshipRequiresConnector(relationship?: string | null): boolean {
  const kind = inferConnectorKind(relationship);
  return !['none', 'self'].includes(kind);
}


export function buildTreeGovernorDecision(
  kind: string,
  payload: TreeGovernorEventPayload,
  relationshipFamily: KinsCircleRelationshipFamily,
  connectorKind: KinsCircleConnectorKind,
  connectorRequired: boolean
): TreeGovernorDecisionRecord {
  const evidenceId = `TREE-EVIDENCE-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  if (connectorRequired && !payload.connectorSelected && !payload.childId && !payload.parentId && !payload.spouseOrPartnerId) {
    return {
      ruleApplied: `connector-required:${connectorKind}`,
      reason: `${relationshipFamily} relationship requires a ${connectorKind} connector before placement can be trusted.`,
      confidence: 88,
      runtimeEvidenceId: evidenceId,
      suggestedNextAction: `Select or verify the required ${connectorKind} connector.`,
      autoRepairConsidered: true,
    };
  }

  return {
    ruleApplied: `relationship-family:${relationshipFamily}`,
    reason: `${kind} passed Tree Governor relationship inference with ${connectorKind} connector policy.`,
    confidence: 96,
    runtimeEvidenceId: evidenceId,
    suggestedNextAction: 'No repair required. Continue monitoring live tree evidence.',
    autoRepairConsidered: false,
  };
}


export function useTreeGovernorEvent(source = 'tree-ui') {
  function emit(kind: string, payload: TreeGovernorEventPayload) {
    try {
      const relationshipFamily =
        payload.relationshipFamily ?? inferRelationshipFamily(payload.relationship ?? payload.relationshipOverride);

      const connectorKind =
        payload.connectorKind ?? inferConnectorKind(payload.relationship ?? payload.relationshipOverride);

      const connectorRequired =
        payload.connectorRequired ?? relationshipRequiresConnector(payload.relationship ?? payload.relationshipOverride);

      const decision =
        payload.decision ??
        buildTreeGovernorDecision(kind, payload, relationshipFamily, connectorKind, connectorRequired);

      emitTreeGovernorEvent(kind, {
        ...payload,
        source,
        relationshipFamily,
        connectorKind,
        connectorRequired,
        decision,
        ruleApplied: decision.ruleApplied,
        reason: decision.reason,
        confidence: decision.confidence,
        runtimeEvidenceId: decision.runtimeEvidenceId,
        suggestedNextAction: decision.suggestedNextAction,
        autoRepairConsidered: decision.autoRepairConsidered,
        displayName: payload.displayName ?? payload.nickname ?? payload.name ?? null,
        nickname: payload.nickname ?? null,
        generatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.warn('Tree Governor event failed safely:', error);
    }
  }

  return { emit };
}


export function emitRelationshipGovernorEvent(input:{
  action:'relationship-created'|'relationship-updated'|'relationship-removed';
  relationship:string;
  memberId?:string|null;
  relatedMemberId?:string|null;
  connectorId?:string|null;
  nickname?:string|null;
}){

  const family=inferRelationshipFamily(input.relationship);
  const connector=inferConnectorKind(input.relationship);
  const required=relationshipRequiresConnector(input.relationship);

  const decision=buildTreeGovernorDecision(
    input.action,
    {
      relationship:input.relationship,
      connectorSelected:!!input.connectorId,
      nickname:input.nickname??null,
      memberId:input.memberId??null,
      relatedMemberId:input.relatedMemberId??null,
      connectorId:input.connectorId??null,
    } as any,
    family,
    connector,
    required
  );

  emitTreeGovernorEvent(input.action,{
    relationship:input.relationship,
    relationshipFamily:family,
    connectorKind:connector,
    connectorRequired:required,
    memberId:input.memberId??null,
    relatedMemberId:input.relatedMemberId??null,
    connectorId:input.connectorId??null,
    nickname:input.nickname??null,
    decision,
    ruleApplied:decision.ruleApplied,
    reason:decision.reason,
    confidence:decision.confidence,
    runtimeEvidenceId:decision.runtimeEvidenceId,
    suggestedNextAction:decision.suggestedNextAction,
    autoRepairConsidered:decision.autoRepairConsidered,
    generatedAt:new Date().toISOString(),
    source:'tree-relationship-runtime',
  });

}


export function emitTreeStructureGovernorEvent(input:{
  action:
    |'member-deleted'
    |'duplicate-merged'
    |'layout-updated'
    |'governor-repair'
    |'bulk-relationship-update';

  memberId?:string|null;
  relatedMemberId?:string|null;
  relationship?:string|null;
  repairId?:string|null;
  summary?:string|null;
}){

  const relationship=input.relationship ?? "tree-operation";

  const family=inferRelationshipFamily(relationship);
  const connector=inferConnectorKind(relationship);
  const required=relationshipRequiresConnector(relationship);

  const decision=buildTreeGovernorDecision(
    input.action,
    {
      relationship,
      memberId:input.memberId??null,
      relatedMemberId:input.relatedMemberId??null,
    } as any,
    family,
    connector,
    required
  );

  emitTreeGovernorEvent(input.action,{
    relationship,
    relationshipFamily:family,
    connectorKind:connector,
    connectorRequired:required,

    memberId:input.memberId??null,
    relatedMemberId:input.relatedMemberId??null,

    repairId:input.repairId??null,
    summary:input.summary??null,

    decision,

    ruleApplied:decision.ruleApplied,
    reason:decision.reason,
    confidence:decision.confidence,
    runtimeEvidenceId:decision.runtimeEvidenceId,
    suggestedNextAction:decision.suggestedNextAction,
    autoRepairConsidered:decision.autoRepairConsidered,

    source:"tree-structure-runtime",

    generatedAt:new Date().toISOString(),
  });

}
